import type { HTMLAttributes } from 'react'
import { cn } from '@/shared/lib'

// Статус читается формой (точка) + цветом (семантический токен), а не только
// текстом. Тон — из CRM-семантики (won/lost/overdue) и базовой
// (success/info/warning). Заливка — приглушённый тон того же цвета.

export type StatusKind =
  | 'won'
  | 'lost'
  | 'overdue'
  | 'paid'
  | 'issued'
  | 'payment_claimed'
  | 'draft'
  | 'published'
  | 'approved'
  | 'active'
  | 'revoked'
  | 'done'
  | 'open'
  // Нейтральный customer-срез проекта (won/lost заказчику не раскрываем).
  | 'in_progress'
  | 'completed'

interface StatusConfig {
  label: string
  text: string
  dot: string
}

// Фон бейджа — нейтральный (bg-elevated), семантику несут цвет текста + точка.
// Цветной текст на нейтральном тёмном держит контраст AA (тонированный красный
// фон ронял бы красный текст ниже порога).
const STATUS: Record<StatusKind, StatusConfig> = {
  won: { label: 'Выиграна', text: 'text-won', dot: 'bg-won' },
  lost: { label: 'Проиграна', text: 'text-lost', dot: 'bg-lost' },
  overdue: { label: 'Просрочено', text: 'text-overdue', dot: 'bg-overdue' },
  paid: { label: 'Оплачен', text: 'text-success', dot: 'bg-success' },
  issued: { label: 'Выставлен', text: 'text-info', dot: 'bg-info' },
  payment_claimed: {
    label: 'Ожидает подтверждения',
    text: 'text-warning',
    dot: 'bg-warning',
  },
  draft: { label: 'Черновик', text: 'text-lost', dot: 'bg-lost' },
  published: { label: 'Опубликован', text: 'text-text-secondary', dot: 'bg-text-tertiary' },
  approved: { label: 'Согласован', text: 'text-won', dot: 'bg-won' },
  active: { label: 'Активен', text: 'text-success', dot: 'bg-success' },
  revoked: { label: 'Отозван', text: 'text-lost', dot: 'bg-lost' },
  done: { label: 'Закрыта', text: 'text-success', dot: 'bg-success' },
  open: { label: 'Открыта', text: 'text-info', dot: 'bg-info' },
  in_progress: { label: 'В работе', text: 'text-text-secondary', dot: 'bg-text-tertiary' },
  completed: { label: 'Завершена', text: 'text-text-secondary', dot: 'bg-text-tertiary' },
}

interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  status: StatusKind
  label?: string
}

export function StatusBadge({ status, label, className, ...rest }: StatusBadgeProps) {
  const cfg = STATUS[status]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill bg-bg-elevated px-2.5 py-1 font-mono text-xs uppercase tracking-wide',
        cfg.text,
        className,
      )}
      {...rest}
    >
      <span className={cn('size-1.5 rounded-pill', cfg.dot)} aria-hidden="true" />
      {label ?? cfg.label}
    </span>
  )
}
